import React, { useState, useEffect, useRef } from 'react';
import { ofacScreeningState } from './ofacScreening';
import type { NodeInspectorProps } from './types';
import { AlertTriangle, Search, ArrowUpDown, Repeat2, ShieldCheck, ShieldAlert, Loader2, ChevronDown, ChevronRight, FileCode2, ExternalLink, ArrowRight, EyeOff, Crosshair } from 'lucide-react';
import { DataFreshnessTag } from '../ui/DataFreshnessTag';
import { Skeleton, SkeletonList, SkeletonStat } from '../ui/Skeleton';
import { SectionHelpIcon } from '../ui/SectionHelpIcon';
import { FeatureExplainer } from '../ui/FeatureExplainer';
import { FileDropzone } from '../ui/FileDropzone';
import { addressService, identityService, investigationAnalysisService } from '../../services/api';
import api from '../../services/api';
import { safeStr, toErrorMessage } from '../../utils/safeStr';
import { OFAC_INDICATOR_LINE, OFAC_LIST_NAME } from '../../utils/sanctionsCopy';

// ─── DEX swap presentation helpers (P0.6) ───
// Block-explorer tx base per EVM chain. Covers all 14 chains the decoder now
// serves (post chain-widen), not just ETH/MATIC/BNB — so ARB/BASE/OP/AVAX swaps
// link to the right explorer instead of falling back to etherscan.
const DEX_EXPLORER_TX: Record<string, string> = {
    ETH: 'https://etherscan.io/tx/', MATIC: 'https://polygonscan.com/tx/',
    BNB: 'https://bscscan.com/tx/', ARB: 'https://arbiscan.io/tx/',
    BASE: 'https://basescan.org/tx/', OP: 'https://optimistic.etherscan.io/tx/',
    AVAX: 'https://snowtrace.io/tx/', LINEA: 'https://lineascan.build/tx/',
    BLAST: 'https://blastscan.io/tx/', SCROLL: 'https://scrollscan.com/tx/',
    GNOSIS: 'https://gnosisscan.io/tx/', CELO: 'https://celoscan.io/tx/',
    MANTLE: 'https://mantlescan.xyz/tx/', MOONBEAM: 'https://moonscan.io/tx/',
};
const dexTxUrl = (chain: string | null | undefined, hash: string | null | undefined): string | null => {
    if (!hash) return null;
    const c = (chain || 'ETH').toUpperCase();
    const norm = c === 'BSC' ? 'BNB' : c === 'POLYGON' ? 'MATIC' : c;
    return (DEX_EXPLORER_TX[norm] || DEX_EXPLORER_TX.ETH) + hash;
};
// Compact amount; '?' when missing/non-finite.
const fmtSwapAmount = (v: unknown): string => {
    if (v === null || v === undefined) return '?';
    const n = Number(v);
    if (!isFinite(n)) return '?';
    return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};
// A token leg is "obscure/unresolved" when the symbol failed to decode, OR the
// amount came back un-scaled (decimals() failed → decoder returns amount === raw).
// Either makes the displayed amount untrustworthy. Soft heuristic — see DEX_SUPPORT.md.
const swapLegObscure = (symbol: unknown, amount: unknown, amountRaw: unknown): boolean => {
    if (!symbol) return true;
    if (amount !== undefined && amountRaw !== undefined &&
        Number(amount) === Number(amountRaw) && Number(amountRaw) > 0) return true;
    return false;
};
// Safe timestamp → short local string. Handles epoch-seconds, epoch-ms, and ISO.
const fmtSwapTime = (ts: unknown): string => {
    if (ts === null || ts === undefined || ts === '') return '';
    const num = Number(ts);
    const d = isNaN(num) ? new Date(String(ts)) : new Date(num < 1e12 ? num * 1000 : num);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// ─── Accordion Section ───
// Fixes: T1 (layout shift), T4 (stale re-open on SSE reconnect)
// - One-way auto-open: when defaultOpen transitions false→true, section opens smoothly
// - User-collapse tracking: if user manually closes, SSE reconnect won't re-open
// - CSS grid-rows transition: prevents sudden layout shift on late data arrival
const AccordionSection: React.FC<{
    title: string;
    badge?: React.ReactNode;
    help?: string;
    freshness?: string;
    cacheAge?: number;
    loading?: boolean;
    defaultOpen?: boolean;
    variant?: 'default' | 'danger' | 'success';
    /** R-INSPECT-10: Raw API response data for "View Raw" toggle */
    rawData?: unknown;
    /** R-INSPECT-31: Citation context for Section 65B compliance */
    citationSource?: string;
    citationAddress?: string;
    /** Lazy-load callback — fires once when section is first expanded */
    onFirstExpand?: () => void;
    /**
     * When this key changes (e.g. selected node id), reset expand/lazy-load state
     * so switching nodes does not leave a stuck spinner or skip a re-fetch.
     */
    expandKey?: string | null;
    children: React.ReactNode;
}> = ({ title, badge, help, freshness, cacheAge, loading, defaultOpen = false, variant = 'default', rawData, citationSource, citationAddress, onFirstExpand, expandKey, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    const [showRaw, setShowRaw] = useState(false);
    const userCollapsed = useRef(false); // Track if user manually collapsed this section
    const hasExpandedOnce = useRef(defaultOpen); // Track if lazy-load callback already fired
    const prevExpandKey = useRef(expandKey);
    // LE workflow is UI-first: RAW/JSON is opt-in (dev build or localStorage flag).
    const allowRawExport = React.useMemo(() => {
        if (import.meta.env.DEV) return true;
        try { return localStorage.getItem('intel_show_raw') === '1'; } catch { return false; }
    }, []);

    // Node (or other expandKey) change: allow lazy-load to fire again; clear RAW view
    useEffect(() => {
        if (prevExpandKey.current === expandKey) return;
        prevExpandKey.current = expandKey;
        hasExpandedOnce.current = false;
        userCollapsed.current = false;
        setShowRaw(false);
        setOpen(defaultOpen);
        if (defaultOpen) {
            hasExpandedOnce.current = true;
            onFirstExpand?.();
        }
    }, [expandKey, defaultOpen, onFirstExpand]);

    // One-way auto-open: only open (never close) on data arrival
    // Respects user manual collapse — won't re-open on SSE reconnect (T4)
    useEffect(() => {
        if (defaultOpen && !userCollapsed.current) {
            setOpen(true);
            if (!hasExpandedOnce.current) {
                hasExpandedOnce.current = true;
                onFirstExpand?.();
            }
        }
    }, [defaultOpen, onFirstExpand]);

    const handleToggle = () => {
        const nextState = !open;
        setOpen(nextState);
        // Track manual collapse — prevents SSE reconnect from re-opening
        if (!nextState) userCollapsed.current = true;
        // If user re-opens manually, allow future auto-opens again
        if (nextState) userCollapsed.current = false;
        // Fire lazy-load on first expand
        if (nextState && !hasExpandedOnce.current) {
            hasExpandedOnce.current = true;
            onFirstExpand?.();
        }
    };

    const borderColors = {
        default: 'border-border',
        danger: 'border-red-500/40',
        success: 'border-emerald-500/30',
    };

    return (
        <div className={`border rounded-lg overflow-hidden ${borderColors[variant]}`}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-2 w-full text-left px-3 py-2 bg-secondary/30 hover:bg-secondary/60 transition-colors"
            >
                {open ? <ChevronDown className="w-3 h-3 text-foreground/60 shrink-0" /> : <ChevronRight className="w-3 h-3 text-foreground/60 shrink-0" />}
                <span className="text-xs uppercase tracking-wide text-foreground/80 font-semibold flex-1">{title}</span>
                {loading && <Skeleton className="h-3 w-3 shrink-0" />}
                {badge}
                <div className="flex items-center gap-1 shrink-0">
                    {citationSource && citationAddress && open && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const ts = freshness ? new Date(freshness).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST' : new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' IST';
                                const cite = `Source: ${citationSource}, Section: ${title}, Retrieved: ${ts}, Address: ${citationAddress}`;
                                navigator.clipboard.writeText(cite);
                            }}
                            className="px-1.5 py-0.5 text-[10px] font-mono rounded border bg-secondary/50 text-muted-foreground border-border hover:bg-secondary transition-colors"
                            title="Copy Section 65B citation to clipboard"
                        >
                            CITE
                        </button>
                    )}
                    {rawData !== undefined && allowRawExport && open && (
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowRaw(prev => !prev); }}
                            className={`px-1.5 py-0.5 text-[10px] font-mono rounded border transition-colors ${showRaw ? 'bg-primary/20 text-primary border-blue-500/30' : 'bg-secondary/50 text-muted-foreground border-border hover:bg-secondary'}`}
                            title="Optional developer view of API JSON (enable via localStorage intel_show_raw=1)"
                        >
                            {showRaw ? 'RICH' : 'RAW'}
                        </button>
                    )}
                    {help && <SectionHelpIcon content={help} />}
                    {freshness && <DataFreshnessTag fetchedAt={freshness} cacheMaxAgeSeconds={cacheAge} />}
                </div>
            </button>
            {/* CSS grid-rows transition: smooth height animation prevents layout shift (T1) */}
            <div
                className="grid border-t border-border transition-[grid-template-rows] duration-200 ease-out"
                style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
            >
                <div className="overflow-hidden">
                    <div className="px-3 pb-3 pt-1">
                        {showRaw && allowRawExport && rawData !== undefined ? (
                            <pre className="text-[10px] font-mono bg-card text-foreground/80 rounded p-2 overflow-auto max-h-[300px] whitespace-pre-wrap break-all">
                                {JSON.stringify(rawData, null, 2)}
                            </pre>
                        ) : children}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Status Badges ───
const StatusBadge: React.FC<{ variant: 'danger' | 'success' | 'info' | 'warning'; children: React.ReactNode }> = ({ variant, children }) => {
    const styles = {
        danger: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
        success: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
        info: 'bg-primary/10 text-primary border-blue-500/30',
        warning: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30',
    };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium whitespace-nowrap ${styles[variant]}`}>{children}</span>;
};

const SectionSkeleton: React.FC<{ variant?: 'stats' | 'list'; label?: string }> = ({
    variant = 'list',
    label = 'Loading',
}) => {
    if (variant === 'stats') {
        return (
            <div className="mt-2 grid grid-cols-2 gap-1.5" role="status" aria-label={label}>
                {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="bg-secondary/40 rounded px-2 py-1.5">
                        <SkeletonStat />
                    </div>
                ))}
            </div>
        );
    }
    return <SkeletonList count={4} className="mt-2" label={label} />;
};

export const IntelligenceTab: React.FC<NodeInspectorProps> = (props) => {
    const { selectedNode, chain, nodeHealth, dossierOverride, dossier: injectedDossier, onTraceAddress, onDexSwapsLoaded } = props;
    const blockFetches = nodeHealth?.blockFetches ?? false;
    const dossier = dossierOverride || injectedDossier;
    const EVM_CHAINS = new Set(['ETH', 'BNB', 'MATIC', 'ARB', 'BASE', 'OP', 'AVAX', 'LINEA', 'BLAST', 'SCROLL', 'GNOSIS', 'CELO', 'MANTLE', 'MOONBEAM', 'BSC', 'POLYGON']);
    const isEvm = EVM_CHAINS.has(chain || '');
    const isBtc = chain === 'BTC';
    const PRIVACY_CHAINS = new Set(['XMR', 'ZEC', 'DASH']);
    const isPrivacyChain = PRIVACY_CHAINS.has(chain || '') || !!(selectedNode as any)?.is_privacy_chain;

    // Self-loading state
    const [exposureData, setExposureData] = useState<any>(null);
    const [exposureLoading, setExposureLoading] = useState(false);
    const [dexData, setDexData] = useState<any>(null);
    const [dexLoading, setDexLoading] = useState(false);
    const [showAllDex, setShowAllDex] = useState(false);
    const [approvalsData, setApprovalsData] = useState<any[] | null>(null);
    const [approvalsLoading, setApprovalsLoading] = useState(false);
    const [contractData, setContractData] = useState<any>(null);
    const [contractLoading, setContractLoading] = useState(false);
    const [p2pPresence, setP2pPresence] = useState<any>(null);
    const [p2pLoading, setP2pLoading] = useState(false);
    const [identityData, setIdentityData] = useState<any>(null);
    const [identityLoading, setIdentityLoading] = useState(false);
    // Privacy chain investigation state
    const [ringData, setRingData] = useState<any>(null);
    const [ringLoading, setRingLoading] = useState(false);
    const [deepCheckData, setDeepCheckData] = useState<any>(null);
    const [deepCheckLoading, setDeepCheckLoading] = useState(false);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvResult, setCsvResult] = useState<any>(null);
    const [crossChainData, setCrossChainData] = useState<any>(null);
    const [crossChainLoading, setCrossChainLoading] = useState(false);
    /** Bumped on node change — ignore late responses from the previous node (R8). */
    const fetchGenRef = useRef(0);
    const nodeId = selectedNode?.id || null;

    // Reset lazy-load state when node changes
    const prevNodeIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (selectedNode?.id !== prevNodeIdRef.current) {
            prevNodeIdRef.current = selectedNode?.id || null;
            fetchGenRef.current += 1;
            setExposureData(null); setExposureLoading(false);
            setDexData(null); setDexLoading(false); setShowAllDex(false);
            setApprovalsData(null); setApprovalsLoading(false);
            setContractData(null); setContractLoading(false);
            setP2pPresence(null); setP2pLoading(false);
            setIdentityData((selectedNode as any)?._identity ?? null); setIdentityLoading(false);
            setRingData(null); setRingLoading(false);
            setDeepCheckData(null); setDeepCheckLoading(false);
            setCsvResult(null); setCsvImporting(false);
            setCrossChainData(null); setCrossChainLoading(false);
        }
    }, [selectedNode?.id]);

    // Lazy-load: Cross-Chain Activity — fetches on first accordion expand.
    // EVM-only; GoldRush's cross-chain endpoint rejects non-EVM addresses.
    // On success, pushes a summary into useBridgeInsightStore so TriageRibbon
    // can render a top-level "Multi-chain (N)" chip.
    const loadCrossChain = React.useCallback(() => {
        if (blockFetches || !isEvm || !selectedNode?.id || !chain || crossChainData || crossChainLoading) return;
        const gen = fetchGenRef.current;
        const addr = selectedNode.id;
        setCrossChainLoading(true);
        addressService.getCrossChainActivity(chain, addr)
            .then(r => {
                if (gen !== fetchGenRef.current) return;
                setCrossChainData(r);
                if (r && r.supported_count != null) {
                    import('../../stores/useBridgeInsightStore').then(({ useBridgeInsightStore }) => {
                        useBridgeInsightStore.getState().setCrossChain(chain, addr, {
                            total_chains: r.total_chains || 0,
                            supported_count: r.supported_count || 0,
                            preview_chains: (r.chains || [])
                                .filter((c: any) => c.supported)
                                .slice(0, 3)
                                .map((c: any) => c.chain_label || c.chain_name),
                        });
                    });
                }
            })
            .catch(() => {
                if (gen !== fetchGenRef.current) return;
                setCrossChainData(null);
            })
            .finally(() => {
                if (gen !== fetchGenRef.current) return;
                setCrossChainLoading(false);
            });
    }, [selectedNode?.id, chain, isEvm, crossChainData, crossChainLoading, blockFetches]);

    // Mirror dossier.bridges into useBridgeInsightStore so TriageRibbon can
    // surface a top-level "🌉 Bridge (N)" chip without re-fetching.
    // NOTE: reads bridgesData off dossier / selectedNode directly to avoid
    // TDZ (bridgesData const is declared ~150 lines below for render use).
    useEffect(() => {
        if (!selectedNode?.id || !chain) return;
        const b = dossier?.sections?.bridges != null ? dossier.sections.bridges : selectedNode?._bridges;
        if (!b) return;
        import('../../stores/useBridgeInsightStore').then(({ useBridgeInsightStore }) => {
            const count = b.total_bridge_txs || (b.bridge_interactions?.length ?? 0);
            useBridgeInsightStore.getState().setBridge(chain, selectedNode.id, count > 0 ? {
                bridge_count: count,
                primary_bridge_family: b.bridge_interactions?.[0]?.bridge_family
                    || b.bridge_interactions?.[0]?.bridge_name || null,
                primary_target_chain: b.bridge_interactions?.[0]?.target_chain || null,
            } : null);
        });
    }, [dossier?.sections?.bridges, selectedNode?.id, selectedNode?._bridges, chain]);

    // Lazy-load: Exposure — fetches on first accordion expand (not on mount)
    const loadExposure = React.useCallback(() => {
        if (blockFetches || !selectedNode?.id || !chain || exposureData || exposureLoading) return;
        const gen = fetchGenRef.current;
        setExposureLoading(true);
        addressService.getExposure(chain, selectedNode.id)
            .then(r => { if (gen === fetchGenRef.current) setExposureData(r); })
            .catch(() => { if (gen === fetchGenRef.current) setExposureData(null); })
            .finally(() => { if (gen === fetchGenRef.current) setExposureLoading(false); });
    }, [selectedNode?.id, chain, exposureData, exposureLoading, blockFetches]);

    // Lazy-load: DEX Swaps — fetches on first accordion expand
    const loadDexSwaps = React.useCallback(() => {
        if (blockFetches || !isEvm || !selectedNode?.id || !chain || dexData || dexLoading) return;
        const gen = fetchGenRef.current;
        setDexLoading(true);
        addressService.getDexSwaps(chain, selectedNode.id)
            .then(r => { if (gen === fetchGenRef.current) setDexData(r); })
            .catch(() => { if (gen === fetchGenRef.current) setDexData(null); })
            .finally(() => { if (gen === fetchGenRef.current) setDexLoading(false); });
    }, [selectedNode?.id, chain, isEvm, dexData, dexLoading, blockFetches]);

    // Notify parent when DEX swap data loads — for graph edge labeling
    useEffect(() => {
        if (dexData?.swaps?.length > 0 && selectedNode?.id && onDexSwapsLoaded) {
            onDexSwapsLoaded(dexData.swaps, selectedNode.id);
        }
    }, [dexData, selectedNode?.id, onDexSwapsLoaded]);

    // Lazy-load: Approvals — fetches on first accordion expand
    const loadApprovals = React.useCallback(() => {
        if (blockFetches || !isEvm || !selectedNode?.id || !chain || approvalsData || approvalsLoading) return;
        const gen = fetchGenRef.current;
        setApprovalsLoading(true);
        addressService.getApprovals(chain, selectedNode.id)
            .then(r => { if (gen === fetchGenRef.current) setApprovalsData(r?.approvals || []); })
            .catch(() => { if (gen === fetchGenRef.current) setApprovalsData(null); })
            .finally(() => { if (gen === fetchGenRef.current) setApprovalsLoading(false); });
    }, [selectedNode?.id, chain, isEvm, approvalsData, approvalsLoading, blockFetches]);

    // Lazy-load: Contract Analysis — fetches on first accordion expand
    const loadContract = React.useCallback(() => {
        if (blockFetches || !isEvm || !selectedNode?.id || !chain || !selectedNode?.is_contract || contractData || contractLoading) return;
        const gen = fetchGenRef.current;
        setContractLoading(true);
        addressService.getContractInfo(chain, selectedNode.id)
            .then(r => { if (gen === fetchGenRef.current) setContractData(r); })
            .catch(() => { if (gen === fetchGenRef.current) setContractData(null); })
            .finally(() => { if (gen === fetchGenRef.current) setContractLoading(false); });
    }, [selectedNode?.id, chain, isEvm, selectedNode?.is_contract, contractData, contractLoading, blockFetches]);

    // Lazy-load: Ring Elimination — runs on first accordion expand (XMR only)
    const loadRingElimination = React.useCallback(() => {
        if (!isPrivacyChain || !selectedNode?.id || ringData || ringLoading) return;
        // Extract tx_hash from node ID (strip "tx:" prefix if present)
        const txHash = selectedNode.id.startsWith('tx:') ? selectedNode.id.slice(3) : selectedNode.id;
        if (txHash.length !== 64) return;
        const gen = fetchGenRef.current;
        setRingLoading(true);
        investigationAnalysisService.ringElimination(txHash)
            .then(r => { if (gen === fetchGenRef.current) setRingData(r); })
            .catch(() => {
                if (gen !== fetchGenRef.current) return;
                setRingData({ error: true, summary: 'Ring elimination unavailable. Import exchange CSVs first to populate known-output database.' });
            })
            .finally(() => { if (gen === fetchGenRef.current) setRingLoading(false); });
    }, [selectedNode?.id, isPrivacyChain, ringData, ringLoading]);

    // Lazy-load: Privacy Deep Check — runs on first accordion expand
    const loadDeepCheck = React.useCallback(() => {
        if (!isPrivacyChain || !selectedNode?.id || deepCheckData || deepCheckLoading) return;
        const txHash = selectedNode.id.startsWith('tx:') ? selectedNode.id.slice(3) : selectedNode.id;
        const timestamp = (selectedNode as any)?.timestamp || new Date().toISOString();
        const gen = fetchGenRef.current;
        setDeepCheckLoading(true);
        investigationAnalysisService.privacyDeepCheck(txHash, chain || 'XMR', timestamp, 30)
            .then(r => { if (gen === fetchGenRef.current) setDeepCheckData(r); })
            .catch(() => {
                if (gen !== fetchGenRef.current) return;
                setDeepCheckData({ error: true, assessment: 'NO_LEADS', intelligence_hits: [] });
            })
            .finally(() => { if (gen === fetchGenRef.current) setDeepCheckLoading(false); });
    }, [selectedNode?.id, chain, isPrivacyChain, deepCheckData, deepCheckLoading]);

    // Exchange CSV import handler
    const handleCsvImportFile = React.useCallback(async (file: File | null) => {
        if (!file) return;
        setCsvImporting(true);
        setCsvResult(null);
        try {
            const result = await investigationAnalysisService.importExchangeCsv(file);
            setCsvResult(result);
            // Clear ring data so it can be re-run with new evidence
            setRingData(null);
        } catch (err: any) {
            setCsvResult({ error: true, message: toErrorMessage(err, 'CSV import failed') });
        } finally {
            setCsvImporting(false);
        }
    }, []);

    const ofacData = dossier?.sections?.ofac || {};
    const ofacFreshness = ofacData?.fetched_at;
    // R10: derived from the CURRENT node's dossier section on every render (the
    // dossier hook owns the stale-response guard, R8), so Node A's result can
    // never label Node B. "unavailable" is NOT "clear".
    const ofacState = ofacScreeningState(dossier?.sections?.ofac, {
        loading: !!dossier?.loadingKeys?.has('ofac'),
        nodeCategory: selectedNode?.entity_category,
    });
    const isSanctioned = ofacState.kind === 'sanctioned';
    const ofacUnavailable = ofacState.kind === 'unavailable';

    // Build fallback profile from node data when profile API fails
    const buildFallbackProfile = () => {
        const n = selectedNode;
        // Use != null to catch zero values (tx_count=0, first_seen=0 are valid)
        const hasAny = n.tx_count != null || n.first_seen != null || n.balance !== undefined || n.last_seen != null;
        if (!hasAny) return null;

        const toSafeTimestamp = (ts: number | string | undefined): Date | null => {
            if (ts == null) return null;
            const num = Number(ts);
            if (isNaN(num)) return null;
            const date = new Date(num < 1e12 ? num * 1000 : num);
            return isNaN(date.getTime()) ? null : date;
        };
        const firstSeen = toSafeTimestamp(n.first_seen);
        const lastSeen = toSafeTimestamp(n.last_seen);
        const ageDays = firstSeen ? Math.floor((Date.now() - firstSeen.getTime()) / 86400000) : null;
        const dormantDays = lastSeen ? Math.floor((Date.now() - lastSeen.getTime()) / 86400000) : null;
        return {
            address_age_days: ageDays,
            dormant_days: dormantDays,
            total_volume: n.total_volume ?? undefined,
            unique_counterparties: n.unique_counterparties ?? undefined,
            tx_count: n.tx_count ?? undefined,
            is_contract: n.is_contract,
            _fallback: true,
        };
    };
    const profileData = dossier?.sections?.profile?.profile || selectedNode._profile || buildFallbackProfile();
    const profileFreshness = dossier?.sections?.profile?.fetched_at;

    // Use != null to avoid treating total_bridge_txs=0 as missing
    const bridgesData = dossier?.sections?.bridges != null ? dossier.sections.bridges : selectedNode?._bridges;
    const bridgesFreshness = dossier?.sections?.bridges?.fetched_at;

    const unlimitedApprovals = approvalsData?.filter((a: any) => a.is_unlimited)?.length || 0;

    // G7 interpretation toggle — persisted in localStorage
    const [showInterpretations, setShowInterpretations] = useState(() => {
        return localStorage.getItem('intelligence-hide-interpretations') !== 'true';
    });
    const toggleInterpretations = () => {
        setShowInterpretations(prev => {
            const next = !prev;
            localStorage.setItem('intelligence-hide-interpretations', next ? 'false' : 'true');
            return next;
        });
    };

    return (
        <div className="text-sm space-y-2 pb-4">
            {/* Toggle bar for interpretations */}
            <div className="flex items-center justify-end px-1">
                <button
                    onClick={toggleInterpretations}
                    className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                >
                    {showInterpretations ? 'Hide explanations' : 'Show explanations'}
                </button>
            </div>

            <FeatureExplainer
                featureKey="intelligence-tab"
                title="Node Intelligence"
                what="Sanctions screening, behavioral profiling, P2P presence, identity lookups, exposure analysis, and bridge detection for the selected address."
                howItWorks="Address Profile, Sanctions, and bridges load automatically when you select a node. Exposure, P2P, and Identity load on expand. All analysis runs on cached transactions — load transactions in the Transfers tab first for best results."
                interpret="Red banners = sanctions matches. High in/out ratio = collection behavior. Bridge hops = cross-chain movement. P2P hits = linked to INR trading. Identity hits = real-world attribution."
            />

            {/* ─── Address Profile ─── */}
            <AccordionSection
                expandKey={nodeId}
                title="Address Profile"
                badge={profileData
                    ? <StatusBadge variant="info">{profileData.is_contract ? 'Contract' : 'EOA'}{profileData.address_age_days ? ` · ${profileData.address_age_days}d` : ''}</StatusBadge>
                    : undefined}
                help="Behavioral profile — address age, volume, counterparty count, dormancy and more."
                freshness={profileFreshness}
                cacheAge={86400}
                loading={dossier?.loadingKeys?.has('profile')}
                defaultOpen={true}
                citationSource="GoPlus Security API" citationAddress={selectedNode?.id}
            >
                {profileData ? (
                    <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-2 gap-1.5 text-xs">
                            {profileData.is_contract !== undefined && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Type</span>
                                    <span className="font-medium">{profileData.is_contract ? 'Contract' : 'EOA'}</span>
                                </div>
                            )}
                            {profileData.address_age_days != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Age</span>
                                    <span className="font-medium">{profileData.address_age_days} days</span>
                                </div>
                            )}
                            {profileData.dormant_days != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Dormant</span>
                                    <span className={profileData.dormant_days > 365 ? 'text-red-500 font-bold' : 'font-medium'}>{profileData.dormant_days}d</span>
                                </div>
                            )}
                            {profileData.total_volume != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Volume</span>
                                    <span className="font-mono font-medium">{Number(profileData.total_volume).toLocaleString()}</span>
                                </div>
                            )}
                            {profileData.unique_counterparties != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Counterparties</span>
                                    <span className="font-medium">{profileData.unique_counterparties}</span>
                                </div>
                            )}
                            {profileData.in_out_ratio != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">In/Out Ratio</span>
                                    <span className="font-mono font-medium">{Number(profileData.in_out_ratio).toFixed(2)}</span>
                                </div>
                            )}
                            {profileData.tx_frequency_per_day != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Tx/Day</span>
                                    <span className="font-mono font-medium">{Number(profileData.tx_frequency_per_day).toFixed(2)}</span>
                                </div>
                            )}
                            {profileData.tx_count != null && (
                                <div className="bg-secondary/40 rounded px-2 py-1.5">
                                    <span className="text-[10px] text-muted-foreground block">Transactions</span>
                                    <span className="font-mono font-medium">{Number(profileData.tx_count).toLocaleString()}</span>
                                </div>
                            )}
                            {profileData.nonce_gap_count != null && profileData.nonce_gap_count > 0 && (
                                <div className="bg-orange-500/10 rounded px-2 py-1.5 border border-orange-500/20">
                                    <span className="text-[10px] text-muted-foreground block">Nonce Gaps</span>
                                    <span className="text-orange-600 dark:text-orange-400 font-bold">{profileData.nonce_gap_count} ⚠️</span>
                                </div>
                            )}
                        </div>

                        {/* ─── Activity Pattern (incoming/outgoing/last_seen) ─── */}
                        {(profileData.incoming_count != null || profileData.outgoing_count != null || profileData.last_seen) && (
                            <div className="border-t border-border/40 pt-2">
                                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block mb-1">Activity Pattern</span>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    {profileData.incoming_count != null && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Incoming Txs</span>
                                            <span className="font-mono font-medium">{Number(profileData.incoming_count).toLocaleString()}</span>
                                        </div>
                                    )}
                                    {profileData.outgoing_count != null && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Outgoing Txs</span>
                                            <span className="font-mono font-medium">{Number(profileData.outgoing_count).toLocaleString()}</span>
                                        </div>
                                    )}
                                    {typeof profileData.last_seen === 'string' && profileData.last_seen.length > 0 && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5 col-span-2">
                                            <span className="text-[10px] text-muted-foreground block">Last Seen</span>
                                            <span className="font-medium">{profileData.last_seen}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── Value Analysis (avg_value, max_value, top3_concentration) ─── */}
                        {(profileData.avg_value != null || profileData.max_value != null || profileData.top3_concentration != null) && (
                            <div className="border-t border-border/40 pt-2">
                                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block mb-1">Value Analysis</span>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    {profileData.avg_value != null && isFinite(Number(profileData.avg_value)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Avg Value</span>
                                            <span className="font-mono font-medium">{Number(profileData.avg_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                        </div>
                                    )}
                                    {profileData.max_value != null && isFinite(Number(profileData.max_value)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Max Value</span>
                                            <span className="font-mono font-medium">{Number(profileData.max_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                                        </div>
                                    )}
                                    {profileData.top3_concentration != null && isFinite(Number(profileData.top3_concentration)) && (
                                        <div className={`rounded px-2 py-1.5 ${Number(profileData.top3_concentration) > 0.8 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-secondary/40'}`}>
                                            <span className="text-[10px] text-muted-foreground block">Top-3 Concentration</span>
                                            <span className={`font-mono font-medium ${Number(profileData.top3_concentration) > 0.8 ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                                                {(Number(profileData.top3_concentration) * 100).toFixed(1)}%
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── Token Diversity ─── */}
                        {(profileData.token_diversity != null || profileData.unique_tokens != null) && (
                            <div className="border-t border-border/40 pt-2">
                                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block mb-1">Token Diversity</span>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    {profileData.unique_tokens != null && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Unique Tokens</span>
                                            <span className="font-medium">{Array.isArray(profileData.unique_tokens) ? profileData.unique_tokens.length : Number(profileData.unique_tokens)}</span>
                                        </div>
                                    )}
                                    {profileData.token_diversity != null && isFinite(Number(profileData.token_diversity)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Diversity Index</span>
                                            <span className="font-mono font-medium">{Number(profileData.token_diversity).toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── EVM Technical (avg_gas_used, nonce_gaps detail) ─── */}
                        {isEvm && (profileData.avg_gas_used != null) && (
                            <div className="border-t border-border/40 pt-2">
                                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block mb-1">EVM Technical</span>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    {profileData.avg_gas_used != null && isFinite(Number(profileData.avg_gas_used)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Avg Gas Used</span>
                                            <span className="font-mono font-medium">{Number(profileData.avg_gas_used).toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ─── BTC Forensics (consolidation, multi_output, balance, received/sent) ─── */}
                        {isBtc && (profileData.consolidation_txs != null || profileData.multi_output_txs != null || profileData.balance != null || profileData.total_received != null) && (
                            <div className="border-t border-border/40 pt-2">
                                <span className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider block mb-1">BTC Forensics</span>
                                <div className="grid grid-cols-2 gap-1.5 text-xs">
                                    {profileData.balance != null && isFinite(Number(profileData.balance)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Balance</span>
                                            <span className="font-mono font-medium">{Number(profileData.balance).toLocaleString(undefined, { maximumFractionDigits: 8 })} BTC</span>
                                        </div>
                                    )}
                                    {profileData.total_received != null && isFinite(Number(profileData.total_received)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Total Received</span>
                                            <span className="font-mono font-medium">{Number(profileData.total_received).toLocaleString(undefined, { maximumFractionDigits: 8 })}</span>
                                        </div>
                                    )}
                                    {profileData.total_sent != null && isFinite(Number(profileData.total_sent)) && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Total Sent</span>
                                            <span className="font-mono font-medium">{Number(profileData.total_sent).toLocaleString(undefined, { maximumFractionDigits: 8 })}</span>
                                        </div>
                                    )}
                                    {profileData.consolidation_txs != null && Number(profileData.consolidation_txs) > 0 && (
                                        <div className="bg-amber-500/10 rounded px-2 py-1.5 border border-amber-500/20">
                                            <span className="text-[10px] text-muted-foreground block">Consolidation Txs</span>
                                            <span className="text-amber-600 dark:text-amber-400 font-bold">{Number(profileData.consolidation_txs)}</span>
                                        </div>
                                    )}
                                    {profileData.multi_output_txs != null && Number(profileData.multi_output_txs) > 0 && (
                                        <div className="bg-secondary/40 rounded px-2 py-1.5">
                                            <span className="text-[10px] text-muted-foreground block">Multi-Output Txs</span>
                                            <span className="font-mono font-medium">{Number(profileData.multi_output_txs)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {profileData.is_multisig && (
                            <div className="p-1.5 bg-primary/10 rounded text-foreground text-xs flex items-center gap-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                                <span><strong>Multi-sig wallet</strong>: requires multiple signatures</span>
                            </div>
                        )}
                        {profileData._fallback && (
                            <div className="text-[10px] text-muted-foreground text-center mt-1 italic">
                                Partial profile from triage data — full profile API unavailable
                            </div>
                        )}
                    </div>
                ) : dossier?.loadingKeys?.has('profile') ? (
                    <SectionSkeleton variant="stats" label="Loading profile data" />
                ) : (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                        Profile data unavailable
                    </p>
                )}
                {/* G7: Interpretation */}
                {profileData && showInterpretations && (
                    <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                        {profileData.address_age_days != null && profileData.address_age_days < 30
                            ? 'Very new address (< 30 days) — recently created addresses are higher risk for fraud or mule activity.'
                            : profileData.dormant_days != null && profileData.dormant_days > 180
                            ? `Address dormant for ${profileData.dormant_days} days — long dormancy followed by sudden activity can indicate compromised accounts.`
                            : profileData.in_out_ratio != null && profileData.in_out_ratio > 5
                            ? 'High in/out ratio — this address receives significantly more than it sends, consistent with collection/accumulation behavior.'
                            : profileData.in_out_ratio != null && profileData.in_out_ratio < 0.2
                            ? 'Low in/out ratio — this address sends significantly more than it receives, consistent with distribution/relay behavior.'
                            : profileData.tx_frequency_per_day != null && profileData.tx_frequency_per_day > 10
                            ? 'High transaction frequency — automated or high-volume activity that may indicate a service or bot.'
                            : 'Profile metrics are within normal ranges. No immediate behavioral anomalies detected from on-chain data.'}
                    </p>
                )}
            </AccordionSection>

            {/* ─── Sanctions ─── */}
            <AccordionSection
                expandKey={nodeId}
                title="Sanctions Screening"
                badge={dossier?.loadingKeys?.has('ofac')
                    ? undefined
                    : isSanctioned
                    ? <StatusBadge variant="danger">SANCTIONED</StatusBadge>
                    : ofacUnavailable
                    ? <StatusBadge variant="info">Unavailable</StatusBadge>
                    : <StatusBadge variant="success">Clear</StatusBadge>}
                help="Checks this address against the US Treasury OFAC SDN sanctions list."
                freshness={ofacFreshness}
                cacheAge={3600}
                loading={dossier?.loadingKeys?.has('ofac')}
                defaultOpen={isSanctioned}
                variant={isSanctioned ? 'danger' : ofacUnavailable ? 'default' : 'success'}
                rawData={ofacData}
                citationSource="OFAC SDN Database" citationAddress={selectedNode?.id}
            >
                {dossier?.loadingKeys?.has('ofac') ? (
                    <SectionSkeleton variant="list" label="Screening sanctions" />
                ) : isSanctioned ? (
                    <div className="p-3 bg-[hsl(var(--risk-critical))] rounded-lg mt-2 sanctions-pulse" data-testid="ofac-sanctioned-card">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-red-600" />
                            <span className="font-bold text-red-700 dark:text-red-300 text-sm"><span aria-hidden="true">★</span> OFAC SANCTIONED</span>
                        </div>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                            This address is on the {OFAC_LIST_NAME}. {OFAC_INDICATOR_LINE}
                        </p>
                        {selectedNode.entity_name && (
                            <p className="text-xs font-semibold text-red-800 dark:text-red-200 mt-1">Entity: {safeStr(selectedNode.entity_name)}</p>
                        )}
                        {ofacData?.hit?.program && (
                            <p className="text-[11px] text-red-600/80 mt-1 font-mono">Hit: {ofacData.hit.entity} ({ofacData.hit.program})</p>
                        )}
                        {ofacData?.hit?.typology && (
                            <p className="text-[11px] text-red-700 dark:text-red-300 mt-1">Designation: {ofacData.hit.typology}</p>
                        )}
                    </div>
                ) : ofacState.kind === 'unavailable' ? (
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="ofac-unavailable">
                        <div className="flex items-center gap-2">
                            <ShieldAlert className="w-4 h-4" />
                            <span className="font-medium">Sanctions check unavailable</span>
                        </div>
                        <p className="mt-1 text-[11px]">{ofacState.reason}. This address has NOT been confirmed clear; screen it again before relying on it.</p>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                        <ShieldCheck className="w-4 h-4" />
                        <span>No active OFAC sanctions detected</span>
                    </div>
                )}
                {/* G7: Interpretation */}
                {showInterpretations && !dossier?.loadingKeys?.has('ofac') && <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                    {isSanctioned
                        ? `This address matches the OFAC SDN list. ${OFAC_INDICATOR_LINE}`
                        : ofacUnavailable
                        ? 'The sanctions screen could not be completed, so this address is neither confirmed clear nor flagged.'
                        : 'This address has been screened against the US Treasury OFAC SDN sanctions list — no matches found. This is a positive indicator for compliance.'}
                </p>}
            </AccordionSection>

            {/* ─── Contract Analysis (EVM contracts only) ─── */}
            {isEvm && selectedNode?.is_contract && (
                <AccordionSection
                expandKey={nodeId}
                    title="Contract Analysis"
                    badge={contractData?.has_source
                        ? <StatusBadge variant="success">Verified</StatusBadge>
                        : contractData && !contractLoading
                            ? <StatusBadge variant="warning">Unverified</StatusBadge>
                            : !contractData && !contractLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                    help="Smart contract metadata — deployer, compiler, proxy status, ABI summary, and source code preview from Etherscan."
                    loading={contractLoading}
                    defaultOpen={!!contractData}
                    onFirstExpand={loadContract}
                    rawData={contractData}
                    citationSource="Etherscan V2 API" citationAddress={selectedNode?.id}
                >
                    {contractData ? (
                        <div className="mt-2 space-y-2">
                            <div className="grid grid-cols-2 gap-1.5 text-xs">
                                {contractData.contract_name && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5 col-span-2">
                                        <span className="text-[10px] text-muted-foreground block">Contract Name</span>
                                        <span className="font-medium flex items-center gap-1">
                                            <FileCode2 className="w-3 h-3" />
                                            {contractData.contract_name}
                                        </span>
                                    </div>
                                )}
                                {contractData.deployer && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5 col-span-2">
                                        <span className="text-[10px] text-muted-foreground block">Deployer</span>
                                        <a
                                            href={`https://${chain === 'BSC' ? 'bscscan.com' : chain === 'POLYGON' ? 'polygonscan.com' : 'etherscan.io'}/address/${contractData.deployer}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[11px] text-primary dark:text-primary hover:underline inline-flex items-center gap-1"
                                        >
                                            {contractData.deployer.slice(0, 10)}...{contractData.deployer.slice(-8)}
                                            <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                    </div>
                                )}
                                {contractData.creation_tx && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5 col-span-2">
                                        <span className="text-[10px] text-muted-foreground block">Creation Tx</span>
                                        <a
                                            href={`https://${chain === 'BSC' ? 'bscscan.com' : chain === 'POLYGON' ? 'polygonscan.com' : 'etherscan.io'}/tx/${contractData.creation_tx}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-[11px] text-primary dark:text-primary hover:underline inline-flex items-center gap-1"
                                        >
                                            {contractData.creation_tx.slice(0, 10)}...{contractData.creation_tx.slice(-8)}
                                            <ExternalLink className="w-2.5 h-2.5" />
                                        </a>
                                    </div>
                                )}
                                {contractData.compiler_version && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5">
                                        <span className="text-[10px] text-muted-foreground block">Compiler</span>
                                        <span className="font-mono font-medium text-[11px]">{contractData.compiler_version.replace(/^v/, '')}</span>
                                    </div>
                                )}
                                {contractData.license_type && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5">
                                        <span className="text-[10px] text-muted-foreground block">License</span>
                                        <span className="font-medium">{contractData.license_type}</span>
                                    </div>
                                )}
                                {contractData.is_proxy && (
                                    <div className="bg-amber-500/10 rounded px-2 py-1.5 border border-amber-500/20 col-span-2">
                                        <span className="text-[10px] text-muted-foreground block">Proxy Contract</span>
                                        <span className="text-amber-600 dark:text-amber-400 font-medium">
                                            Yes{contractData.implementation ? ` → ${contractData.implementation.slice(0, 10)}...${contractData.implementation.slice(-6)}` : ''}
                                        </span>
                                    </div>
                                )}
                                {contractData.abi_available && (
                                    <div className="bg-secondary/40 rounded px-2 py-1.5 col-span-2">
                                        <span className="text-[10px] text-muted-foreground block">ABI</span>
                                        <span className="font-medium">
                                            {contractData.abi_method_count != null ? `${contractData.abi_method_count} methods` : ''}
                                            {contractData.abi_method_count != null && contractData.abi_event_count != null ? ', ' : ''}
                                            {contractData.abi_event_count != null ? `${contractData.abi_event_count} events` : ''}
                                            {contractData.abi_method_count == null && contractData.abi_event_count == null ? 'Available' : ''}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {contractData.source_preview && (
                                <div>
                                    <div className="text-[10px] text-muted-foreground mb-1">Source Preview</div>
                                    <pre className="text-[10px] font-mono bg-slate-100 dark:bg-muted rounded p-2 overflow-x-auto max-h-24 whitespace-pre-wrap break-all leading-relaxed">
                                        {contractData.source_preview}
                                    </pre>
                                </div>
                            )}
                        </div>
                    ) : contractLoading ? (
                        <SectionSkeleton variant="stats" label="Loading contract data" />
                    ) : (
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Contract data unavailable.
                        </p>
                    )}
                </AccordionSection>
            )}

            {/* ─── Exposure ─── */}
            <AccordionSection
                expandKey={nodeId}
                title="Exposure Breakdown"
                badge={Array.isArray(exposureData?.sources) && exposureData.sources.length > 0
                    ? <StatusBadge variant="warning">{exposureData.sources.length} sources</StatusBadge>
                    : !exposureData && !exposureLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                help="Shows where funds come from (sources) and where they go (destinations). High-risk exposure is a red flag."
                loading={exposureLoading}
                defaultOpen={Array.isArray(exposureData?.sources) && exposureData.sources.length > 0}
                rawData={exposureData}
                citationSource="CryptoFlow Exposure Analysis" citationAddress={selectedNode?.id}
                onFirstExpand={loadExposure}
            >
                {Array.isArray(exposureData?.sources) && exposureData.sources.length > 0 ? (
                    <div className="mt-2 text-xs space-y-2">
                        <div>
                            <div className="text-[11px] text-muted-foreground mb-1">Sources (where funds come from)</div>
                            {exposureData.sources.slice(0, 5).map((s: any, i: number) => (
                                <div key={i} className="flex items-center gap-2 mb-0.5">
                                    <div className="flex-1">
                                        <div className="flex justify-between text-xs">
                                            <span>{s.category || s.label || 'Unknown'}</span>
                                            <span className="font-mono">{((s.percentage ?? 0) * 100).toFixed(1)}%</span>
                                        </div>
                                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${s.risk_level === 'high' ? 'bg-red-500' : s.risk_level === 'medium' ? 'bg-amber-500' : 'bg-green-500'}`}
                                                style={{ width: `${Math.min((s.percentage ?? 0) * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {exposureData.destinations?.length > 0 && (
                            <div>
                                <div className="text-[11px] text-muted-foreground mb-1">Destinations (where funds go)</div>
                                {exposureData.destinations.slice(0, 5).map((d: any, i: number) => (
                                    <div key={i} className="flex items-center gap-2 mb-0.5">
                                        <div className="flex-1">
                                            <div className="flex justify-between text-xs">
                                                <span>{d.category || d.label || 'Unknown'}</span>
                                                <span className="font-mono">{((d.percentage ?? 0) * 100).toFixed(1)}%</span>
                                            </div>
                                            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full ${d.risk_level === 'high' ? 'bg-red-500' : d.risk_level === 'medium' ? 'bg-amber-500' : 'bg-green-500'}`}
                                                    style={{ width: `${Math.min((d.percentage ?? 0) * 100, 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : exposureLoading ? (
                    <SectionSkeleton variant="list" label="Loading exposure" />
                ) : (
                    <div className="mt-2 text-center space-y-1">
                        <p className="text-xs text-muted-foreground">Exposure data unavailable.</p>
                        <p className="text-[10px] text-muted-foreground/70">Load transactions first (Transfers tab → Load), then re-open this section. Exposure analysis categorises counterparties as Exchange, Mixer, DeFi, etc.</p>
                    </div>
                )}
                {/* G7: Interpretation */}
                {showInterpretations && Array.isArray(exposureData?.sources) && exposureData.sources.length > 0 && (() => {
                    const highRiskSources = exposureData.sources.filter((s: any) => s.risk_level === 'high');
                    const mixerPct = exposureData.sources.filter((s: any) => (s.category || '').toLowerCase().includes('mixer')).reduce((sum: number, s: any) => sum + (s.percentage ?? 0), 0);
                    return (
                        <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                            {mixerPct > 0.2
                                ? `${(mixerPct * 100).toFixed(0)}% of incoming funds originate from mixer services — this is a significant risk indicator suggesting deliberate obfuscation.`
                                : highRiskSources.length > 0
                                ? `${highRiskSources.length} high-risk source${highRiskSources.length > 1 ? 's' : ''} detected in fund origins. Further investigation of these sources is recommended.`
                                : 'Fund sources appear to be from known or low-risk categories. No significant exposure risk detected.'}
                        </p>
                    );
                })()}
            </AccordionSection>

            {/* ─── Bridges ─── */}
            {(
                <AccordionSection
                expandKey={nodeId}
                    title="Bridging Activity"
                    badge={(bridgesData?.total_bridge_txs ?? 0) > 0
                        ? <StatusBadge variant="warning">{bridgesData!.total_bridge_txs} hops</StatusBadge>
                        : <StatusBadge variant="success">None</StatusBadge>}
                    help="Detects cross-chain bridge hops — a common fund laundering technique."
                    freshness={bridgesFreshness}
                    cacheAge={21600}
                    loading={dossier?.loadingKeys?.has('bridges')}
                    defaultOpen={(bridgesData?.total_bridge_txs ?? 0) > 0}
                    rawData={bridgesData}
                    citationSource="GoPlus Bridge Detection" citationAddress={selectedNode?.id}
                >
                    {bridgesData && bridgesData.total_bridge_txs > 0 ? (
                        <div className="mt-2 text-xs space-y-1.5">
                            {/* Privacy chain bridge alert */}
                            {(() => {
                                const PRIV_CHAINS = ['XMR', 'ZEC', 'DASH', 'PIRATE', 'FIRO'];
                                const privacyBridges = (bridgesData.bridge_interactions || []).filter((b: any) =>
                                    PRIV_CHAINS.includes((b.target_chain || '').toUpperCase()) ||
                                    PRIV_CHAINS.includes((b.source_chain || chain || '').toUpperCase())
                                );
                                return privacyBridges.length > 0 ? (
                                    <div className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                                        <span className="text-red-400 text-sm shrink-0 mt-0.5">&#9888;</span>
                                        <div>
                                            <p className="text-xs font-semibold text-red-300">Privacy Chain Bridge Detected</p>
                                            <p className="text-[10px] text-muted-foreground mt-0.5">
                                                {privacyBridges.length} bridge interaction{privacyBridges.length > 1 ? 's' : ''} involve{privacyBridges.length === 1 ? 's' : ''} a privacy chain
                                                ({privacyBridges.map((b: any) => b.target_chain || b.source_chain).filter(Boolean).join(', ')}).
                                                The transparent-side address should be added to canvas for continued tracing.
                                            </p>
                                        </div>
                                    </div>
                                ) : null;
                            })()}
                            {bridgesData.bridge_interactions?.slice(0, 5).map((b: any, i: number) => (
                                <div key={i} className="flex items-center gap-1 bg-indigo-100/50 dark:bg-indigo-900/30 px-2 py-1 rounded">
                                    <span className="font-medium flex-1 min-w-0 truncate">{b.bridge_name || 'Unknown Bridge'} → {b.target_chain || 'Unknown'}</span>
                                    <span className={`shrink-0 font-mono text-[11px] ${b.direction === 'outgoing' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{(b.direction || 'unknown').toUpperCase()}</span>
                                    {b.target_chain && b.direction === 'outgoing' && !['MULTI', 'AZTEC'].includes(b.target_chain) && onTraceAddress && (
                                        <button
                                            onClick={() => onTraceAddress(selectedNode.id, b.target_chain)}
                                            className="shrink-0 flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-indigo-600 hover:bg-indigo-700 text-foreground rounded transition-colors"
                                            title={`Continue investigation on ${b.target_chain} using same address`}
                                        >
                                            Continue <ArrowRight className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            {bridgesData.bridge_interactions?.length > 5 && (
                                <div className="text-[11px] text-center text-indigo-600 dark:text-indigo-400">+{bridgesData.bridge_interactions.length - 5} more</div>
                            )}
                            <p className="text-[11px] text-indigo-600 dark:text-indigo-400 italic">&#9888;&#65039; Funds may continue on target chain</p>
                        </div>
                    ) : dossier?.loadingKeys?.has('bridges') ? (
                        <SectionSkeleton variant="list" label="Detecting bridge hops" />
                    ) : (
                        <div className="mt-2 text-center space-y-1">
                            <p className="text-xs text-muted-foreground">
                                No cross-chain bridge activity detected.
                            </p>
                            <p className="text-[10px] text-muted-foreground/70">Bridge activity appears when an address has interacted with cross-chain protocols (Wormhole, Multichain, Stargate, etc.). Most common on ETH/BSC/MATIC addresses.</p>
                        </div>
                    )}
                    {/* G7: Interpretation */}
                    {showInterpretations && <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                        {bridgesData && bridgesData.total_bridge_txs > 0
                            ? `${bridgesData.total_bridge_txs} cross-chain bridge transaction${bridgesData.total_bridge_txs > 1 ? 's' : ''} detected. Cross-chain movement is a common technique to obscure fund trails. Investigate the destination chain to continue tracing.`
                            : 'Funds appear to remain on a single blockchain, which simplifies tracing.'}
                    </p>}
                </AccordionSection>
            )}

            {/* ─── Cross-Chain Activity (EVM only) ─── */}
            {isEvm && (
                <AccordionSection
                expandKey={nodeId}
                    title="Cross-Chain Activity"
                    badge={crossChainData?.supported_count > 1
                        ? <StatusBadge variant="warning">{crossChainData.supported_count} chains</StatusBadge>
                        : crossChainData && crossChainData.supported_count <= 1 ? <StatusBadge variant="success">Single chain</StatusBadge>
                        : !crossChainData && !crossChainLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                    help="Shows every chain this EVM address has interacted with, based on GoldRush's cross-chain index. Click a chain to load the same address on that chain."
                    loading={crossChainLoading}
                    defaultOpen={false}
                    rawData={crossChainData}
                    citationSource="GoldRush Cross-Chain Activity" citationAddress={selectedNode?.id}
                    onFirstExpand={loadCrossChain}
                >
                    {crossChainData && Array.isArray(crossChainData.chains) && crossChainData.chains.length > 0 ? (
                        <div className="mt-2 space-y-1.5">
                            <div className="flex flex-wrap gap-1.5">
                                {crossChainData.chains.map((c: any, i: number) => {
                                    const lastSeenDays = c.last_seen_at
                                        ? Math.floor((Date.now() - new Date(c.last_seen_at).getTime()) / 86400000)
                                        : null;
                                    const dayLabel = lastSeenDays == null ? ''
                                        : lastSeenDays === 0 ? 'today'
                                        : lastSeenDays === 1 ? '1d ago'
                                        : lastSeenDays < 30 ? `${lastSeenDays}d ago`
                                        : lastSeenDays < 365 ? `${Math.floor(lastSeenDays / 30)}mo ago`
                                        : `${Math.floor(lastSeenDays / 365)}y ago`;
                                    const clickable = c.supported && c.internal_chain && onTraceAddress;
                                    return (
                                        <button
                                            key={i}
                                            onClick={clickable ? () => onTraceAddress(selectedNode.id, c.internal_chain) : undefined}
                                            disabled={!clickable}
                                            title={clickable
                                                ? `Load ${selectedNode.id.slice(0, 10)}… on ${c.chain_label}`
                                                : `${c.chain_label} — not yet supported for drill-in`}
                                            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-colors ${
                                                clickable
                                                    ? 'bg-indigo-100/50 dark:bg-indigo-900/30 border-indigo-500/30 hover:bg-indigo-200/60 dark:hover:bg-indigo-800/50 cursor-pointer'
                                                    : 'bg-muted/30 border-border/40 text-muted-foreground cursor-not-allowed'
                                            }`}
                                        >
                                            <span className="font-medium">{c.chain_label}</span>
                                            {dayLabel && <span className="text-[10px] opacity-70">· {dayLabel}</span>}
                                            {clickable && <ArrowRight className="w-3 h-3" />}
                                        </button>
                                    );
                                })}
                            </div>
                            {crossChainData.supported_count < crossChainData.total_chains && (
                                <p className="text-[10px] text-muted-foreground/70">
                                    {crossChainData.total_chains - crossChainData.supported_count} chain{crossChainData.total_chains - crossChainData.supported_count > 1 ? 's' : ''} discovered but not yet supported for drill-in.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="mt-2 text-center space-y-1">
                            {crossChainLoading ? (
                                <SectionSkeleton variant="list" label="Searching chains" />
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    {crossChainData?.error ? 'Cross-chain lookup unavailable.'
                                    : crossChainData ? 'Address active only on this chain.'
                                    : 'Expand to discover which other chains this address has touched.'}
                                </p>
                            )}
                        </div>
                    )}
                    {showInterpretations && crossChainData?.supported_count > 1 && (
                        <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                            Same address used on {crossChainData.supported_count} chains — often indicates a power user, bridge-hopper, or an operator managing multi-chain treasury. Click any chain chip to continue investigation on that chain with the same address.
                        </p>
                    )}
                </AccordionSection>
            )}

            {/* ─── P2P Presence ─── */}
            <AccordionSection
                expandKey={nodeId}
                title="P2P Presence"
                badge={p2pPresence?.found
                    ? <StatusBadge variant="warning">{p2pPresence.total_hits} hit{p2pPresence.total_hits !== 1 ? 's' : ''}</StatusBadge>
                    : p2pPresence && !p2pPresence.found ? <StatusBadge variant="success">None</StatusBadge>
                    : !p2pPresence && !p2pLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                help="Checks if this wallet address appears in P2P exchange advertisements or Telegram trading channels."
                loading={p2pLoading}
                defaultOpen={p2pPresence?.found === true}
                rawData={p2pPresence}
                citationSource="P2P Intelligence DB" citationAddress={selectedNode?.id}
                onFirstExpand={() => {
                    if (blockFetches || !selectedNode?.id || p2pPresence || p2pLoading) return;
                    const gen = fetchGenRef.current;
                    setP2pLoading(true);
                    api.get(`/p2p/address/${encodeURIComponent(selectedNode.id)}/presence`)
                        .then(r => { if (gen === fetchGenRef.current) setP2pPresence(r.data); })
                        .catch(() => {
                            if (gen !== fetchGenRef.current) return;
                            setP2pPresence({ found: false, p2p_ads: [], telegram_hits: [], upi_links: [], total_hits: 0 });
                        })
                        .finally(() => { if (gen === fetchGenRef.current) setP2pLoading(false); });
                }}
            >
                {p2pPresence?.found ? (
                    <div className="mt-2 text-xs space-y-2">
                        {/* P2P Ads */}
                        {p2pPresence.p2p_ads?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Exchange Ads ({p2pPresence.p2p_ads.length})</div>
                                {p2pPresence.p2p_ads.slice(0, 5).map((ad: any, i: number) => (
                                    <div key={i} className="flex items-center gap-2 bg-yellow-100/50 dark:bg-yellow-900/20 px-2 py-1.5 rounded mb-1">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-semibold uppercase">{ad.platform}</span>
                                        <span className="font-medium flex-1 min-w-0 truncate">{ad.trader}</span>
                                        {ad.rate && <span className="text-[11px] font-mono text-green-400">₹{ad.rate.toFixed(2)}</span>}
                                        {ad.upi_id && <span className="text-[10px] text-primary font-mono">UPI: {ad.upi_id}</span>}
                                    </div>
                                ))}
                                {p2pPresence.p2p_ads.length > 5 && <div className="text-[11px] text-center text-yellow-600 dark:text-yellow-400">+{p2pPresence.p2p_ads.length - 5} more</div>}
                            </div>
                        )}
                        {/* Telegram */}
                        {p2pPresence.telegram_hits?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Telegram Mentions ({p2pPresence.telegram_hits.length})</div>
                                {p2pPresence.telegram_hits.slice(0, 3).map((t: any, i: number) => (
                                    <div key={i} className="bg-sky-100/50 dark:bg-sky-900/20 px-2 py-1.5 rounded mb-1">
                                        <span className="text-[10px] text-sky-400">#{t.channel}</span>
                                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{t.content}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        {/* UPI Links */}
                        {p2pPresence.upi_links?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">Linked UPI IDs ({p2pPresence.upi_links.length})</div>
                                {p2pPresence.upi_links.map((l: any, i: number) => (
                                    <div key={i} className="flex items-center gap-2 bg-primary/10 px-2 py-1 rounded mb-1">
                                        <span className="font-mono text-primary text-[11px]">{l.upi_id}</span>
                                        <span className="text-[10px] text-muted-foreground">{l.link_type}</span>
                                        {l.confidence && <span className="text-[10px] text-muted-foreground">{(l.confidence * 100).toFixed(0)}%</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="mt-2 text-center space-y-1">
                        {p2pLoading ? (
                            <SectionSkeleton variant="list" label="Searching P2P data" />
                        ) : (
                            <>
                                <p className="text-xs text-muted-foreground">
                                    No P2P presence found for this address.
                                </p>
                                <p className="text-[10px] text-muted-foreground/70">We scan P2P exchange ads (WazirX, Paxful, LocalBitcoins), Telegram trading channels, and scraped UPI-to-crypto links. Hits here mean the wallet was advertised for INR↔crypto trading.</p>
                            </>
                        )}
                    </div>
                )}
            </AccordionSection>

            {/* ─── Identity & OSINT ─── */}
            <AccordionSection
                expandKey={nodeId}
                title="Identity & OSINT"
                badge={identityData?.summary?.length > 0
                    ? <StatusBadge variant="warning">{identityData.summary.length} finding{identityData.summary.length !== 1 ? 's' : ''}</StatusBadge>
                    : identityData && identityData.summary?.length === 0 ? <StatusBadge variant="success">None</StatusBadge>
                    : !identityData && !identityLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                help="Cross-references this wallet against Telegram P2P channels, exchange ads, and breach databases to find linked phone numbers, emails, and real names."
                loading={identityLoading}
                defaultOpen={identityData?.linked_contacts?.length > 0 || identityData?.identity_results?.length > 0}
                rawData={identityData}
                citationSource="Identity Intelligence DB" citationAddress={selectedNode?.id}
                onFirstExpand={() => {
                    if (blockFetches || !selectedNode?.id || !chain || identityData || identityLoading) return;
                    const gen = fetchGenRef.current;
                    const node = selectedNode;
                    setIdentityLoading(true);
                    identityService.getWalletIdentity(chain, node.id)
                        .then(r => {
                            if (gen !== fetchGenRef.current) return;
                            setIdentityData(r);
                            // Persist on node for cross-component access (next-best-action, LE report)
                            if (node) (node as any)._identity = r;
                        })
                        .catch(() => {
                            if (gen !== fetchGenRef.current) return;
                            const empty = { linked_contacts: [], identity_results: [], summary: [] };
                            setIdentityData(empty);
                            if (node) (node as any)._identity = empty;
                        })
                        .finally(() => { if (gen === fetchGenRef.current) setIdentityLoading(false); });
                }}
            >
                {identityData ? (
                    <div className="mt-2 text-xs space-y-3">
                        {/* Summary */}
                        {identityData.summary?.length > 0 && (
                            <div className="bg-amber-100/50 dark:bg-amber-900/20 border border-amber-500/20 rounded px-2 py-1.5">
                                {identityData.summary.map((s: string, i: number) => (
                                    <div key={i} className="text-[11px] text-amber-700 dark:text-amber-300">{s}</div>
                                ))}
                            </div>
                        )}

                        {/* Linked Contacts */}
                        {identityData.linked_contacts?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">
                                    Linked Contacts ({identityData.linked_contacts.length})
                                </div>
                                {identityData.linked_contacts.map((c: any, i: number) => (
                                    <div key={i} className="flex items-center gap-2 bg-sky-100/50 dark:bg-sky-900/20 px-2 py-1 rounded mb-1">
                                        <span className={`text-[10px] px-1 py-0.5 rounded font-semibold uppercase ${
                                            c.type === 'phone' ? 'bg-green-500/20 text-green-400' : 'bg-primary/20 text-primary'
                                        }`}>{c.type}</span>
                                        <span className="font-mono text-[11px] flex-1 min-w-0 truncate">{c.value}</span>
                                        <span className="text-[10px] text-muted-foreground">{c.source}</span>
                                        {c.context && <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">{c.context}</span>}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Telecom / Corporate attribution (TraceFlow — subscriber, carrier, GST/PAN) */}
                        {identityData.telecom_identities?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">
                                    Subscriber Attribution ({identityData.telecom_identities.length})
                                </div>
                                {identityData.telecom_identities.map((t: any, i: number) => (
                                    <div key={i} className="bg-amber-100/50 dark:bg-amber-900/20 border border-amber-500/10 px-2 py-1.5 rounded mb-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-[11px]">{t.phone}</span>
                                            {t.carrier && t.carrier !== 'Unknown' && (
                                                <span className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-500 font-semibold">{t.carrier}</span>
                                            )}
                                            {t.circle && t.circle !== 'India' && (
                                                <span className="text-[10px] text-muted-foreground">{t.circle}</span>
                                            )}
                                            {t.tafcop_sim_count > 1 && (
                                                <span className="text-[10px] text-orange-500">TAFCOP: {t.tafcop_sim_count} SIMs</span>
                                            )}
                                        </div>
                                        {t.subscriber_name && (
                                            <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 mt-0.5">
                                                {t.subscriber_name}
                                                {t.father_name && <span className="font-normal text-muted-foreground"> (S/O {t.father_name})</span>}
                                            </div>
                                        )}
                                        {(t.gstin || t.pan || t.trade_name) && (
                                            <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                                {t.trade_name && <span className="font-medium">{t.trade_name}</span>}
                                                {t.gstin && <span className="font-mono">GSTIN: {t.gstin}</span>}
                                                {t.pan && <span className="font-mono">PAN: {t.pan}</span>}
                                            </div>
                                        )}
                                        {t.registered_address && (
                                            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{t.registered_address}</div>
                                        )}
                                        <div className="text-[9px] text-muted-foreground/70 mt-0.5">{t.source}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Identity Pivot Results (breach data → real names) */}
                        {identityData.identity_results?.length > 0 && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">
                                    Breach Database Matches
                                </div>
                                {identityData.identity_results.map((ir: any, i: number) => (
                                    <div key={i} className="bg-red-100/50 dark:bg-red-900/20 border border-red-500/10 px-2 py-1.5 rounded mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[11px]">{ir.query_value}</span>
                                            <span className="text-[10px] text-muted-foreground">{ir.hit_count} DB{ir.hit_count !== 1 ? 's' : ''}</span>
                                        </div>
                                        {ir.names?.length > 0 && (
                                            <div className="text-[11px] font-semibold text-red-600 dark:text-red-400 mt-0.5">
                                                Names: {ir.names.join(', ')}
                                            </div>
                                        )}
                                        {ir.india_sources?.length > 0 && (
                                            <div className="text-[10px] text-orange-500 mt-0.5">
                                                India: {ir.india_sources.join(', ')}
                                            </div>
                                        )}
                                        {ir.addresses?.length > 0 && (
                                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                                Addr: {ir.addresses.slice(0, 2).join('; ')}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* BitcoinWhosWho Web Mentions */}
                        {identityData.web_mentions && (
                            <div>
                                <div className="text-[10px] text-muted-foreground font-semibold uppercase mb-1">
                                    BitcoinWhosWho
                                </div>
                                <div className={`px-2 py-1.5 rounded ${
                                    identityData.web_mentions.risk_score > 0.3
                                        ? 'bg-red-100/50 dark:bg-red-900/20 border border-red-500/20'
                                        : 'bg-zinc-100/50 dark:bg-zinc-800/50'
                                }`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[11px]">Risk: {identityData.web_mentions.risk_score?.toFixed(1) ?? 'N/A'}</span>
                                        {identityData.web_mentions.scam_reports?.length > 0 && (
                                            <span className="text-[10px] text-red-400">{identityData.web_mentions.scam_reports.length} scam report(s)</span>
                                        )}
                                    </div>
                                    {identityData.web_mentions.associated_urls?.length > 0 && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                            Found on {identityData.web_mentions.associated_urls.length} website(s)
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {identityData.linked_contacts?.length === 0 && !identityData.web_mentions && identityData.identity_results?.length === 0 && (
                            <div className="text-center space-y-1">
                                <p className="text-xs text-muted-foreground">No identity intelligence found for this address.</p>
                                <p className="text-[10px] text-muted-foreground/70">We cross-reference wallets against Telegram channels, P2P ads, breach databases, and BitcoinWhosWho. Hits reveal linked phone numbers, emails, real names, and scam reports.</p>
                            </div>
                        )}
                    </div>
                ) : identityLoading ? (
                    <SectionSkeleton variant="list" label="Searching identity databases" />
                ) : (
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                        Identity data unavailable.
                    </p>
                )}
            </AccordionSection>

            {/* ─── Privacy Chain: Ring Elimination (XMR) ─── */}
            {isPrivacyChain && chain === 'XMR' && (
                <AccordionSection
                expandKey={nodeId}
                    title="Ring Elimination Analysis"
                    badge={ringData && !ringData.error
                        ? <StatusBadge variant={ringData.overall_elimination_pct > 50 ? 'warning' : 'info'}>
                            {ringData.overall_elimination_pct?.toFixed(0)}% eliminated
                        </StatusBadge>
                        : !ringData && !ringLoading
                        ? <span className="text-[10px] text-muted-foreground italic">expand to run</span>
                        : undefined}
                    help="Cross-references ring members against known outputs from exchange records. Import exchange CSVs to populate the known-output database, then re-run."
                    loading={ringLoading}
                    defaultOpen={!!ringData && !ringData.error && ringData.overall_elimination_pct > 0}
                    rawData={ringData}
                    citationSource="Known-Output Ring Elimination" citationAddress={selectedNode?.id}
                    onFirstExpand={loadRingElimination}
                >
                    {ringData && !ringData.error ? (
                        <div className="mt-2 text-xs space-y-2">
                            <p className="text-[11px] text-muted-foreground">{ringData.summary}</p>

                            {ringData.per_input?.map((input: any, i: number) => (
                                <div key={i} className="bg-primary/10 border border-primary/20 rounded px-2 py-1.5">
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-[10px]">Input #{input.input_index}</span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {input.known_members}/{input.ring_size} known
                                        </span>
                                    </div>
                                    {input.key_image && (
                                        <div className="font-mono text-[9px] text-muted-foreground/60 truncate mt-0.5">
                                            Key image: {input.key_image.slice(0, 24)}...
                                        </div>
                                    )}
                                    <p className="text-[10px] mt-1">{input.assessment}</p>
                                    {input.known_details?.length > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                            {input.known_details.map((kd: any, j: number) => (
                                                <div key={j} className="flex items-center gap-1.5 text-[9px] text-muted-foreground">
                                                    <span className="px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">KNOWN</span>
                                                    <span className="font-mono truncate">{kd.tx_hash?.slice(0, 16)}...</span>
                                                    {kd.block_no && <span>block {kd.block_no}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {input.residual_candidates != null && (
                                        <div className="mt-1 flex items-center gap-1">
                                            <div className="h-1.5 flex-1 rounded-full bg-zinc-700 overflow-hidden">
                                                <div className="h-full rounded-full bg-primary transition-all"
                                                    style={{ width: `${input.elimination_pct}%` }} />
                                            </div>
                                            <span className="text-[9px] text-primary font-medium shrink-0">
                                                {input.residual_candidates} left
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}

                            {ringData.caveats?.length > 0 && (
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5">
                                    <p className="text-[10px] font-semibold text-amber-400 mb-0.5">Caveats</p>
                                    {ringData.caveats.map((c: string, i: number) => (
                                        <p key={i} className="text-[10px] text-muted-foreground">{c}</p>
                                    ))}
                                </div>
                            )}

                            {/* CSV import to add more known outputs */}
                            <div className="border-t border-border/50 pt-2 space-y-1.5">
                                <FileDropzone
                                    bare
                                    compact
                                    accept=".csv,text/csv"
                                    disabled={csvImporting}
                                    onFileChange={(f) => { void handleCsvImportFile(f); }}
                                    title="Exchange CSV"
                                    description=".csv"
                                />
                                {csvImporting && (
                                    <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                        <Loader2 className="w-3 h-3 animate-spin" /> Importing…
                                    </p>
                                )}
                                {csvResult && (
                                    <div className={`mt-1.5 text-[10px] px-2 py-1 rounded ${csvResult.error ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                                        {csvResult.error
                                            ? csvResult.message || 'Import failed'
                                            : `Imported ${csvResult.evidence_count} records, ${csvResult.hashes_found} TX hashes found`}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : ringData?.error ? (
                        <div className="mt-2 text-xs space-y-2">
                            <p className="text-[11px] text-muted-foreground">{ringData.summary || 'No ring member data available.'}</p>
                            <FileDropzone
                                bare
                                compact
                                accept=".csv,text/csv"
                                disabled={csvImporting}
                                onFileChange={(f) => { void handleCsvImportFile(f); }}
                                title="Exchange CSV"
                                description=".csv"
                            />
                            {csvImporting && (
                                <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Importing…
                                </p>
                            )}
                            {csvResult && (
                                <div className={`mt-1.5 text-[10px] px-2 py-1 rounded ${csvResult.error ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                                    {csvResult.error
                                        ? csvResult.message || 'Import failed'
                                        : `Imported ${csvResult.evidence_count} records. Click section header to re-run elimination.`}
                                </div>
                            )}
                        </div>
                    ) : ringLoading ? (
                        <SectionSkeleton variant="list" label="Running ring elimination" />
                    ) : null}
                </AccordionSection>
            )}

            {/* ─── Privacy Chain: Deep Intelligence Check ─── */}
            {isPrivacyChain && (
                <AccordionSection
                expandKey={nodeId}
                    title="Privacy Deep Check"
                    badge={deepCheckData && !deepCheckData.error
                        ? <StatusBadge variant={
                            deepCheckData.assessment?.includes('LEAD') && deepCheckData.assessment !== 'NO_LEADS' ? 'warning'
                            : deepCheckData.assessment === 'NO_LEADS' ? 'success' : 'info'
                        }>
                            {deepCheckData.assessment?.replace(/_/g, ' ') || 'checked'}
                        </StatusBadge>
                        : !deepCheckData && !deepCheckLoading
                        ? <span className="text-[10px] text-muted-foreground italic">expand to run</span>
                        : undefined}
                    help="Runs 4 parallel checks: canvas address correlation, exchange timing correlation, THORChain swap detection, and P2P/no-KYC exchange correlation. Generates ranked subpoena leads."
                    loading={deepCheckLoading}
                    defaultOpen={!!deepCheckData && !deepCheckData.error && deepCheckData.intelligence_hits?.length > 0}
                    rawData={deepCheckData}
                    citationSource="Privacy Chain Deep Intelligence" citationAddress={selectedNode?.id}
                    onFirstExpand={loadDeepCheck}
                >
                    {deepCheckData && !deepCheckData.error ? (
                        <div className="mt-2 text-xs space-y-2">
                            {/* Assessment banner */}
                            <div className={`flex items-center gap-2 px-2 py-1.5 rounded ${
                                deepCheckData.assessment?.includes('LEAD') && deepCheckData.assessment !== 'NO_LEADS'
                                    ? 'bg-amber-500/10 border border-amber-500/20'
                                    : 'bg-zinc-500/10 border border-zinc-500/20'
                            }`}>
                                <Crosshair className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                <span className="text-[11px] font-medium">
                                    {deepCheckData.assessment?.replace(/_/g, ' ') || 'Analysis complete'}
                                </span>
                            </div>

                            {/* Intelligence hits */}
                            {deepCheckData.intelligence_hits?.length > 0 && (
                                <div className="space-y-1">
                                    <p className="text-[10px] text-muted-foreground font-semibold uppercase">
                                        Intelligence Hits ({deepCheckData.intelligence_hits.length})
                                    </p>
                                    {deepCheckData.intelligence_hits.map((hit: any, i: number) => (
                                        <div key={i} className="bg-amber-500/10 border border-amber-500/15 rounded px-2 py-1.5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] px-1 py-0.5 rounded font-semibold uppercase bg-amber-500/20 text-amber-400">
                                                    {hit.type || hit.check_type || 'hit'}
                                                </span>
                                                <span className="text-[11px] flex-1 min-w-0 truncate">{hit.description || hit.summary}</span>
                                            </div>
                                            {hit.address && (
                                                <div className="font-mono text-[9px] text-muted-foreground/70 mt-0.5 truncate">
                                                    {hit.chain && <span className="text-[9px] font-semibold mr-1">{hit.chain}</span>}
                                                    {hit.address}
                                                </div>
                                            )}
                                            {hit.confidence && (
                                                <div className="text-[9px] text-muted-foreground mt-0.5">
                                                    Confidence: {typeof hit.confidence === 'number' ? `${(hit.confidence * 100).toFixed(0)}%` : hit.confidence}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Ranked leads */}
                            {deepCheckData.ranked_leads?.length > 0 && (
                                <div className="space-y-1">
                                    <p className="text-[10px] text-muted-foreground font-semibold uppercase">
                                        Ranked Subpoena Leads
                                    </p>
                                    {deepCheckData.ranked_leads.map((lead: any, i: number) => (
                                        <div key={i} className="flex items-center gap-2 bg-primary/10 border border-blue-500/15 rounded px-2 py-1">
                                            <span className="text-[10px] font-bold text-primary shrink-0">#{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-[11px] font-medium">{safeStr(lead.target) || safeStr(lead.entity_name)}</span>
                                                {lead.reason && <span className="text-[10px] text-muted-foreground ml-1.5">{safeStr(lead.reason)}</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Subpoena guidance */}
                            {deepCheckData.subpoena_guidance && (
                                <div className="bg-primary/10 border border-primary/20 rounded px-2 py-1.5">
                                    <p className="text-[10px] font-semibold text-primary mb-0.5">Subpoena Guidance</p>
                                    <p className="text-[10px] text-muted-foreground">{deepCheckData.subpoena_guidance}</p>
                                </div>
                            )}

                            {/* No leads */}
                            {deepCheckData.assessment === 'NO_LEADS' && (!deepCheckData.intelligence_hits || deepCheckData.intelligence_hits.length === 0) && (
                                <p className="text-xs text-muted-foreground text-center">
                                    No correlation hits found within the time window. Try expanding the window or adding more addresses to the canvas for cross-chain correlation.
                                </p>
                            )}
                        </div>
                    ) : deepCheckData?.error ? (
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                            Privacy deep check unavailable for this transaction.
                        </p>
                    ) : deepCheckLoading ? (
                        <SectionSkeleton variant="list" label="Running privacy deep check" />
                    ) : null}
                </AccordionSection>
            )}

            {/* ─── DEX Swaps (EVM) ─── */}
            {isEvm && (
                <AccordionSection
                expandKey={nodeId}
                    title="DEX Swaps"
                    badge={dexData?.swaps?.length > 0
                        ? <StatusBadge variant="info">{dexData.total_swaps ?? dexData.swaps.length} swaps</StatusBadge>
                        : !dexData && !dexLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                    help="DEX token-to-token trades. High swap volume with obscure tokens may indicate wash trading."
                    loading={dexLoading}
                    defaultOpen={dexData?.swaps?.length > 0}
                    rawData={dexData}
                    citationSource="On-chain DEX log decoder (event-signature based)" citationAddress={selectedNode?.id}
                    onFirstExpand={loadDexSwaps}
                >
                    {dexData?.swaps?.length > 0 ? (
                        <div className="mt-2 text-xs space-y-1.5">
                            <div className="text-[11px] text-muted-foreground">Protocols: {dexData.dex_protocols?.join(', ') || '—'}</div>
                            {(showAllDex ? dexData.swaps : dexData.swaps.slice(0, 5)).map((swap: any, i: number) => {
                                const obscure = swapLegObscure(swap.token_in_symbol, swap.amount_in, swap.amount_in_raw)
                                    || swapLegObscure(swap.token_out_symbol, swap.amount_out, swap.amount_out_raw);
                                const url = dexTxUrl(chain, swap.tx_hash);
                                const when = fmtSwapTime(swap.timestamp);
                                return (
                                    <div key={swap.tx_hash ? `${swap.tx_hash}-${i}` : i} className="bg-violet-100/50 dark:bg-violet-900/30 px-2 py-1 rounded">
                                        <div className="flex justify-between items-center gap-2">
                                            <span className="font-mono text-[11px] truncate">
                                                {fmtSwapAmount(swap.amount_in)} {swap.token_in_symbol || '?'}
                                                <span className="text-violet-500"> → </span>
                                                {fmtSwapAmount(swap.amount_out)} {swap.token_out_symbol || '?'}
                                            </span>
                                            <span className="text-[11px] text-muted-foreground whitespace-nowrap">{swap.dex || swap.dex_name || 'DEX'}</span>
                                        </div>
                                        <div className="flex justify-between items-center gap-2 mt-0.5 min-h-[14px]">
                                            <span>
                                                {obscure && (
                                                    <span className="text-[9px] text-amber-600 dark:text-amber-400 inline-flex items-center gap-0.5" title="Token symbol/decimals failed to decode — the amount may be unscaled. Verify the token contract before relying on it.">
                                                        <AlertTriangle className="w-2.5 h-2.5" /> obscure token
                                                    </span>
                                                )}
                                            </span>
                                            <span className="flex items-center gap-2 text-[10px] text-muted-foreground whitespace-nowrap">
                                                {when && <span>{when}</span>}
                                                {url && (
                                                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-violet-600 dark:text-violet-400 hover:underline" title="View transaction on block explorer">
                                                        <ExternalLink className="w-2.5 h-2.5" />
                                                    </a>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            {dexData.swaps.length > 5 && (
                                <button
                                    type="button"
                                    onClick={() => setShowAllDex(v => !v)}
                                    className="w-full text-[11px] text-center text-violet-600 dark:text-violet-400 hover:underline py-0.5"
                                >
                                    {showAllDex ? 'Show less' : `Show all ${dexData.swaps.length}`}
                                </button>
                            )}
                            {dexData.swaps.some((s: any) => swapLegObscure(s.token_in_symbol, s.amount_in, s.amount_in_raw) || swapLegObscure(s.token_out_symbol, s.amount_out, s.amount_out_raw)) && (
                                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 leading-relaxed border-t border-amber-500/20 pt-1">
                                    ⚠ Some swaps involve unresolved/obscure tokens — symbol or decimals failed to decode, so amounts may be unscaled. Verify token contracts before relying on amounts. Aggregator/multi-hop routes show the first leg only.
                                </p>
                            )}
                        </div>
                    ) : dexLoading ? (
                        <SectionSkeleton variant="list" label="Loading DEX swaps" />
                    ) : (
                        <p className="text-xs text-muted-foreground mt-2 text-center">No DEX swap activity detected.</p>
                    )}
                    {/* G7: Interpretation */}
                    {showInterpretations && <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                        {dexData?.swaps?.length > 0
                            ? `${dexData.total_swaps ?? dexData.swaps.length} DEX swap${(dexData.total_swaps ?? dexData.swaps.length) > 1 ? 's' : ''} detected. Token swaps can be used to convert between assets to obscure fund origin. Check if swaps involve obscure or low-liquidity tokens.`
                            : 'No decentralized exchange swaps detected. Address does not appear to use DEX protocols for token conversion.'}
                    </p>}
                </AccordionSection>
            )}

            {/* ─── Token Approvals (EVM) ─── */}
            {isEvm && (
                <AccordionSection
                expandKey={nodeId}
                    title="Token Approvals"
                    badge={approvalsData && approvalsData.length > 0
                        ? <StatusBadge variant={unlimitedApprovals > 0 ? 'danger' : 'info'}>{approvalsData.length} active{unlimitedApprovals > 0 ? ` · ${unlimitedApprovals} ∞` : ''}</StatusBadge>
                        : !approvalsData && !approvalsLoading ? <span className="text-[10px] text-muted-foreground italic">expand to load</span> : undefined}
                    help="Smart contracts approved to spend tokens. Unlimited approvals to unknown contracts are a security risk."
                    loading={approvalsLoading}
                    defaultOpen={unlimitedApprovals > 0}
                    rawData={approvalsData}
                    citationSource="GoPlus Token Approval API" citationAddress={selectedNode?.id}
                    onFirstExpand={loadApprovals}
                >
                    {approvalsData && approvalsData.length > 0 ? (
                        <div className="mt-2 text-xs space-y-1.5">
                            {approvalsData.slice(0, 5).map((appr: any, i: number) => (
                                <div key={i} className="flex justify-between items-center bg-rose-100/50 dark:bg-rose-900/30 px-2 py-1 rounded">
                                    <div>
                                        <span className="font-mono text-[11px]">{safeStr(appr.token_symbol) || 'Token'}</span>
                                        <span className="text-[11px] text-muted-foreground ml-1">→ {appr.spender_label || `${(appr.spender || '').slice(0, 8)}...`}</span>
                                    </div>
                                    <span className={`text-[11px] font-mono ${appr.is_unlimited ? 'text-red-600 dark:text-red-400 font-bold' : 'text-muted-foreground'}`}>
                                        {appr.is_unlimited ? '∞ UNLIMITED' : appr.allowance_display || 'Limited'}
                                    </span>
                                </div>
                            ))}
                            {approvalsData.length > 5 && (
                                <div className="text-[11px] text-center text-rose-600 dark:text-rose-400">+{approvalsData.length - 5} more</div>
                            )}
                            {unlimitedApprovals > 0 && (
                                <p className="text-[11px] text-rose-600 dark:text-rose-400 italic">Unlimited approvals: spender can drain tokens</p>
                            )}
                        </div>
                    ) : approvalsLoading ? (
                        <SectionSkeleton variant="list" label="Loading token approvals" />
                    ) : (
                        <p className="text-xs text-muted-foreground mt-2 text-center">No active token approvals found.</p>
                    )}
                    {/* G7: Interpretation */}
                    {showInterpretations && <p className="text-[10px] text-muted-foreground mt-2 italic leading-relaxed border-t border-border/50 pt-1.5">
                        {unlimitedApprovals > 0
                            ? `${unlimitedApprovals} unlimited token approval${unlimitedApprovals > 1 ? 's' : ''} found. Unlimited approvals allow spender contracts to drain tokens at any time — this is a significant security risk and potential indicator of phishing or rug pull exposure.`
                            : approvalsData && approvalsData.length > 0
                            ? `${approvalsData.length} active approval${approvalsData.length > 1 ? 's' : ''} found, all with limited allowances. This is normal for DeFi usage.`
                            : 'No active token approvals. This address has not granted spending permissions to external contracts.'}
                    </p>}
                </AccordionSection>
            )}
        </div>
    );
};
